/**
 * @fileoverview Context for mobile/tablet filter sheet
 * @module shared/contexts/FilterSheetContext
 */

import { createContext, useContext, useState, ReactNode, ReactElement, useCallback } from 'react'

type FilterSection = 'preset' | 'range' | 'aggregation' | 'profitBasis' | null

interface FilterSheetContextValue {
  isOpen: boolean
  activeSection: FilterSection
  open: (section?: FilterSection) => void
  close: () => void
}

const FilterSheetContext = createContext<FilterSheetContextValue | undefined>(undefined)

export function FilterSheetProvider({ children }: { children: ReactNode }): ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<FilterSection>(null)

  const open = useCallback((section: FilterSection = null) => {
    setActiveSection(section)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    setActiveSection(null)
  }, [])

  return (
    <FilterSheetContext.Provider value={{ isOpen, activeSection, open, close }}>
      {children}
    </FilterSheetContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFilterSheet(): FilterSheetContextValue {
  const context = useContext(FilterSheetContext)
  if (!context) {
    throw new Error('useFilterSheet must be used within a FilterSheetProvider')
  }
  return context
}
